import type { ColDef, TooltipCallbackFunc } from 'ag-grid-community';
import { Array as A, pipe } from 'effect';

// The path is split at runtime, so each step reads an untyped value; `field` itself is checked
// against the row type by the callers.
const readFieldStep = (value: unknown, key: string): unknown =>
  value !== null && typeof value === 'object' ? (value as Record<string, unknown>)[key] : undefined;

// Replacement for the `tooltipField` colDef option deprecated in AG Grid 36.2: the tooltip shows the
// raw value of the row data under `field` (dot notation reads nested values, as `tooltipField` did),
// not the formatted cell value that `tooltip: true` would show.
export const tooltipFromField =
  <TData>(field: NonNullable<ColDef<TData>['field']>): TooltipCallbackFunc<TData> =>
  ({ data }) =>
    pipe(field.split('.'), A.reduce<unknown, string>(data, readFieldStep));

export const getFieldTooltipProps = <TData>(
  column: ColDef<TData>,
  tooltipFieldByTypes: string[] = [],
): Pick<ColDef<TData>, 'tooltip'> => {
  const tooltipTypeExist = tooltipFieldByTypes.includes(column.type as string);

  return tooltipTypeExist && column.field
    ? {
        tooltip: tooltipFromField<TData>(column.field),
      }
    : {};
};

export const addTooltipPropsForColDefs =
  (tooltipFieldByTypes: string[]) =>
  <TData>(columns: ColDef<TData>[]): ColDef<TData>[] =>
    columns.map((column) => ({
      ...column,
      ...getFieldTooltipProps(column, tooltipFieldByTypes),
    }));
